import { useState, useMemo, useCallback, useEffect } from 'react';
import { NPSDSearchFilters } from '@/components/npsd/NPSDSearchFilters';
import { NPSDSearchResults } from '@/components/npsd/NPSDSearchResults';
import { NPSDDateRangePicker } from '@/components/npsd/NPSDDateRangePicker';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { listHasOtherCompanyItems } from '@/utils/call-card-helpers';

import { api } from '@/services/api-class';
import moment from 'moment';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const canCurrentUserAccessCaseReport = _wg('canCurrentUserAccessCaseReport');

const NARRATIVE_SEARCH_ENABLED = true;

interface NPSDSearchReportProps {
  searchMode?: string;
  isMobile?: boolean;
  theme?: string;
  officerLabel?: string;
  onView?: (item: any) => void;
  onAdd?: (item: any) => void;
  onOpenNewTab?: (item: any) => void;
}

export function NPSDSearchReport({
  searchMode = 'default',
  isMobile = false,
  theme = '',
  officerLabel = 'Officer',
  onView,
  onAdd,
  onOpenNewTab,
}: NPSDSearchReportProps) {
  // Search form fields
  const [caseNumber, setCaseNumber] = useState('');
  const [primaryOfficerId, setPrimaryOfficerId] = useState<string | number | null>('');
  const [backupOfficerId, setBackupOfficerId] = useState<string | number | null>('');
  const [primaryOrBackupOfficerId, setPrimaryOrBackupOfficerId] = useState<string | number | null>('');
  const [npsdClientId, setNpsdClientId] = useState<string | number | null>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [address, setAddress] = useState('');
  const [narrative, setNarrative] = useState('');
  const [natureOfCall, setNatureOfCall] = useState<string[]>([]);

  // Dropdown options
  const [officers, setOfficers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [naturesOfCall, setNaturesOfCall] = useState<any[]>([]);

  // Results
  const [results, setResults] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingOptions, setIsLoadingOptions] = useState(true);

  const columns = useMemo(() => [
    { key: 'caseId', label: 'Case Number', field: 'caseId' },
    { key: 'incidentOccurredFrom', label: 'Date/Time', field: 'incidentOccurredFrom' },
    { key: 'npsdClientList', label: 'Clients', field: 'npsdClientList' },
    { key: 'natureOfCallValues', label: 'Nature of Call', field: 'natureOfCallValues' },
    { key: 'primaryOfficerName', label: 'Primary ' + officerLabel, field: 'primaryOfficerName' },
    { key: 'callLocFormattedAddress', label: 'Location', field: 'callLocFormattedAddress', fullWidth: true },
  ], [officerLabel]);

  const showViewButton = useMemo(() => {
    return ['default', 'addviewRpt'].indexOf(searchMode) >= 0;
  }, [searchMode]);

  const showAddButton = useMemo(() => {
    return ['addRpt', 'addviewRpt'].indexOf(searchMode) >= 0;
  }, [searchMode]);

  const officerItems = useMemo(() => {
    return officers.map((o: any) => ({ label: o.name, value: o.id }));
  }, [officers]);

  const clientItems = useMemo(() => {
    return clients.map((c: any) => ({ label: c.name, value: c.id }));
  }, [clients]);

  const nocItems = useMemo(() => {
    return naturesOfCall.map((n: any) => ({ label: n.value, value: n.code, companyId: n.companyId, companyName: n.companyName }));
  }, [naturesOfCall]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (caseNumber) count++;
    if (primaryOfficerId) count++;
    if (backupOfficerId) count++;
    if (primaryOrBackupOfficerId) count++;
    if (npsdClientId) count++;
    if (startDate) count++;
    if (endDate) count++;
    if (address) count++;
    if (NARRATIVE_SEARCH_ENABLED && narrative) count++;
    if (natureOfCall && natureOfCall.length > 0) count++;
    return count;
  }, [caseNumber, primaryOfficerId, backupOfficerId, primaryOrBackupOfficerId, npsdClientId, startDate, endDate, address, narrative, natureOfCall]);

  // Load dropdown options on mount
  useEffect(() => {
    let loaded = 0;
    const checkDone = () => {
      loaded++;
      if (loaded >= 3) {
        setIsLoadingOptions(false);
      }
    };

    // Load officers
    api.getReportSearchOfficers((result: any) => {
      if (result && result.officerNames) {
        setOfficers(result.officerNames.sort((a: any, b: any) =>
          (a.name || '').localeCompare(b.name || '')
        ));
      }
      checkDone();
    });

    // Load clients
    api.listClientGroup((result: any) => {
      if (result && result.npsdClientList) {
        setClients(result.npsdClientList.sort((a: any, b: any) =>
          (a.name || '').localeCompare(b.name || '')
        ));
      }
      checkDone();
    });

    // Load nature of call
    api.listNatureOfCall((result: any) => {
      if (result && result.natureOfCallList) {
        setNaturesOfCall(result.natureOfCallList);
      }
      checkDone();
    }, () => {
      checkDone();
    }, true);
  }, []);

  const canAccessReport = useCallback((item: any): boolean => {
    if (typeof (window as any).canCurrentUserAccessCaseReport === 'function') {
      return canCurrentUserAccessCaseReport(item.author);
    }
    return true;
  }, []);

  const isRowDisabled = useCallback((item: any): boolean => {
    return !canAccessReport(item);
  }, [canAccessReport]);

  const loadResults = useCallback((page: number, size: number) => {
    setIsLoading(true);

    const filters: any[] = [];

    if (caseNumber) {
      filters.push({ field: 'caseId', operator: 'startswith', value: caseNumber.trim() });
    }
    if (primaryOfficerId) {
      filters.push({ field: 'primaryOfficerId', operator: 'eq', value: primaryOfficerId });
    }
    if (backupOfficerId) {
      filters.push({ field: 'backupUnitOfficerId', operator: 'eq', value: backupOfficerId });
    }
    if (primaryOrBackupOfficerId) {
      filters.push({ field: 'primaryOrBackupUnitOfficerId', operator: 'eq', value: primaryOrBackupOfficerId });
    }
    if (npsdClientId) {
      filters.push({ field: 'npsdClientId', operator: 'eq', value: npsdClientId });
    }
    if (startDate) {
      const startMoment = moment(startDate);
      if (startMoment.isValid()) {
        filters.push({ field: 'incidentOccurredFrom', operator: 'gte', value: startMoment.utc().format((window as any).DATETIME_FORMAT_SERVER_UTC) });
      }
    }
    if (endDate) {
      const endMoment = moment(endDate);
      if (endMoment.isValid()) {
        filters.push({ field: 'incidentOccurredFrom', operator: 'lte', value: endMoment.add(23, 'hours').add(59, 'minutes').add(59, 'seconds').add(999, 'milliseconds').utc().format((window as any).DATETIME_FORMAT_SERVER_UTC) });
      }
    }
    if (address) {
      filters.push({ field: 'callLocFormattedAddress', operator: 'contains', value: address.trim() });
    }
    if (NARRATIVE_SEARCH_ENABLED && narrative) {
      filters.push({ field: 'narrative', operator: 'contains', value: narrative.trim() });
    }
    if (natureOfCall && natureOfCall.length > 0) {
      filters.push({ field: 'natureOfCall', operator: 'eq', values: natureOfCall });
    }

    const searchData = {
      data: {
        take: size,
        skip: (page - 1) * size,
        page: page,
        pageSize: size,
        filter: {
          logic: 'and',
          filters: filters,
        },
      },
    };

    api.reportSearch(
      'Report',
      page,
      size.toString(),
      JSON.stringify(searchData),
      (result: any) => {
        setResults(result.entries || []);
        setTotalRecords(result.records || 0);
        setHasSearched(true);
        setIsLoading(false);
      }
    );
  }, [caseNumber, primaryOfficerId, backupOfficerId, primaryOrBackupOfficerId, npsdClientId, startDate, endDate, address, narrative, natureOfCall]);

  const performSearch = useCallback(() => {
    setCurrentPage(1);
    loadResults(1, pageSize);
  }, [loadResults, pageSize]);

  const clearSearch = useCallback(() => {
    setCaseNumber('');
    setPrimaryOfficerId('');
    setBackupOfficerId('');
    setPrimaryOrBackupOfficerId('');
    setNpsdClientId('');
    setStartDate(null);
    setEndDate(null);
    setAddress('');
    setNarrative('');
    setNatureOfCall([]);
    setResults([]);
    setTotalRecords(0);
    setCurrentPage(1);
    setHasSearched(false);
  }, []);

  const handleView = useCallback((item: any) => {
    onView?.(item);
  }, [onView]);

  const handleAdd = useCallback((item: any) => {
    onAdd?.(item);
  }, [onAdd]);

  const handleOpenNewTab = useCallback((item: any) => {
    if (!canAccessReport(item)) return;
    onOpenNewTab?.(item);
  }, [canAccessReport, onOpenNewTab]);

  const handleRowClick = useCallback((item: any) => {
    if (!canAccessReport(item)) return;
    if (searchMode === 'default') {
      handleView(item);
    } else if (showAddButton) {
      handleAdd(item);
    }
  }, [canAccessReport, searchMode, showAddButton, handleView, handleAdd]);

  const onPageChange = useCallback((page: number) => {
    setCurrentPage(page);
    loadResults(page, pageSize);
  }, [loadResults, pageSize]);

  const onPageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    loadResults(1, size);
  }, [loadResults]);

  const formatDateTime = useCallback((dateStr: string): string => {
    if (!dateStr) return '\u2014';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const formatNatureOfCall = useCallback((nocValues: any[]): string => {
    if (!nocValues || !Array.isArray(nocValues) || nocValues.length === 0) {
      return '\u2014';
    }
    return nocValues.map(noc => noc.value || noc).join(', ');
  }, []);

  const formatClients = useCallback((clientList: any[]): string => {
    if (!clientList || !Array.isArray(clientList) || clientList.length === 0) {
      return 'N/A';
    }
    return clientList.map(c => c.name).join(', ');
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  }, [performSearch]);

  const cellRenderers = useMemo(() => ({
    incidentOccurredFrom: (item: any) => formatDateTime(item.incidentOccurredFrom),
    npsdClientList: (item: any) => formatClients(item.npsdClientList),
    natureOfCallValues: (item: any) => formatNatureOfCall(item.natureOfCallValues),
  }), [formatDateTime, formatClients, formatNatureOfCall]);

  const renderCardTitle = useCallback((item: any) => item.caseId, []);

  return (
    <div className={`npsd-search-report ${theme}`}>
      <NPSDSearchFilters
        activeFilterCount={activeFilterCount}
        loading={isLoading || isLoadingOptions}
        onSearch={performSearch}
      >
        <div className="search-fields">
          {/* Date Range Section */}
          <NPSDDateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />

          {/* Other Filters */}
          <div className="field-row">
            <div className="field">
              <label>Case Number</label>
              <input
                type="text"
                value={caseNumber}
                onChange={(e) => setCaseNumber(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Case Number"
              />
            </div>
            <div className="field">
              <label>Location</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter Location"
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Primary {officerLabel}</label>
              <Dropdownlist
                value={primaryOfficerId}
                items={officerItems}
                labelKey="label"
                valueKey="value"
                placeholder={'Select ' + officerLabel}
                clearable={true}
                onChange={setPrimaryOfficerId}
              />
            </div>
            <div className="field">
              <label>Backup {officerLabel}</label>
              <Dropdownlist
                value={backupOfficerId}
                items={officerItems}
                labelKey="label"
                valueKey="value"
                placeholder={'Select ' + officerLabel}
                clearable={true}
                onChange={setBackupOfficerId}
              />
            </div>
            <div className="field">
              <label>Primary or Backup {officerLabel}</label>
              <Dropdownlist
                value={primaryOrBackupOfficerId}
                items={officerItems}
                labelKey="label"
                valueKey="value"
                placeholder={'Select ' + officerLabel}
                clearable={true}
                onChange={setPrimaryOrBackupOfficerId}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Client</label>
              <Dropdownlist
                value={npsdClientId}
                items={clientItems}
                labelKey="label"
                valueKey="value"
                placeholder="Select Client"
                clearable={true}
                onChange={setNpsdClientId}
              />
            </div>
            <div className="field field-wide">
              <label>Nature of Call</label>
              <MultiSelect
                value={natureOfCall}
                items={nocItems}
                labelKey="label"
                valueKey="value"
                groupBy={listHasOtherCompanyItems(nocItems) ? 'companyName' : null}
                groupOrder="insertion"
                placeholder="Select Nature of Call"
                onChange={setNatureOfCall}
              />
            </div>
          </div>
          {NARRATIVE_SEARCH_ENABLED && (
            <div className="field-row">
              <div className="field field-wide">
                <label>Narrative</label>
                <input
                  type="text"
                  value={narrative}
                  onChange={(e) => setNarrative(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter Narrative Text"
                />
              </div>
            </div>
          )}
        </div>
        <div className="search-actions">
          <button type="button" className="btn btn-primary" onClick={performSearch} disabled={isLoading || isLoadingOptions}>
            <i className="fa fa-search"></i> Search
          </button>
          <button type="button" className="btn btn-secondary" onClick={clearSearch}>
            <i className="fa fa-trash"></i> Clear
          </button>
        </div>
      </NPSDSearchFilters>

      {/* Results */}
      <NPSDSearchResults
        columns={columns}
        results={results}
        loading={isLoading}
        hasSearched={hasSearched}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        rowDisabled={isRowDisabled}
        showNewTabButton={showViewButton}
        cellRenderers={cellRenderers}
        renderCardTitle={renderCardTitle}
        onRowClick={handleRowClick}
        onLinkClick={handleView}
        onOpenNewTab={handleOpenNewTab}
        onCurrentPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  );
}

(window as any).NPSDSearchReportReact = NPSDSearchReport;
